import { t } from '@lingui/core/macro';
import { TApplication } from 'lendingApi/applications.types';
import {
  APPLICATION_STATUS,
  APPLICATION_STATUS_VALUE,
} from 'utils/applications/applications.config';
import styles from './ApplicationStatusCell.module.css';
import { Funded } from './Funded/Funded';
import { ManualReview } from './ManualReviewButton/ManualReview';
import { OpenButtonOrLabel } from './OpenButtonOrLabel/OpenButtonOrLabel';
import { OpenInNewTabButton } from './OpenInNewTabButton/OpenInNewTabButton';
import { StatusButtonOrLabel } from './StatusButtonOrLabel';
import { useApplicationStatusCell } from './useApplicationStatusCell.hook';

type TProps = {
  application: TApplication;
};

export const ApplicationStatusCell = ({ application }: TProps) => {
  const status = application?.status;

  const {
    cannotUploadDocuments,
    cannotRefreshOffers,
    cannotInteractWithOffers,
    cannotCreateApplications,
    getDocumentsPath,
    handleClick,
    getApplicationPath,
  } = useApplicationStatusCell();

  const customer = application?.customer;
  const applicationId = application?.application_uid;
  const hasMultipleOffers =
    application?.formal_offer_sets?.[0]?.formal_offers?.length > 1;

  const cannotRefreshApp =
    customer?.has_open_application ||
    customer?.has_active_formal_offer ||
    cannotRefreshOffers;

  const renderClass = () => {
    switch (status) {
      case APPLICATION_STATUS.declined:
      case APPLICATION_STATUS.offer_rejected:
      case APPLICATION_STATUS.deferred:
      case APPLICATION_STATUS.expired:
        return styles.nonActionableStatus;
      default:
        return styles.actionableStatus;
    }
  };

  const renderStatus = () => {
    if (application?.is_partner_channel) {
      if (status === APPLICATION_STATUS.funded) {
        return <Funded application={application} showCommission={false} />;
      }

      return (
        <span className={styles.actionableStatus}>
          {APPLICATION_STATUS_VALUE(status)}
        </span>
      );
    }

    switch (status) {
      case APPLICATION_STATUS.open:
        return (
          <OpenButtonOrLabel
            cannotCreateApp={cannotCreateApplications}
            cannotUploadDocs={cannotUploadDocuments}
            application={application}
            documentsPath={getDocumentsPath(applicationId)}
          />
        );
      case APPLICATION_STATUS.action_required:
        return (
          <StatusButtonOrLabel
            disabled={cannotUploadDocuments}
            disabledLabel={t`Docs required`}
            status={APPLICATION_STATUS.action_required}
            variant="action"
            buttonText={t`Docs required`}
            redirectUrl={getDocumentsPath(applicationId)}
            onClick={handleClick}
          />
        );

      case APPLICATION_STATUS.expired:
        return (
          <StatusButtonOrLabel
            disabled={cannotRefreshApp}
            disabledLabel={t`Offer expired`}
            status={APPLICATION_STATUS.expired}
            variant="denim"
            buttonText={t`Refresh app`}
            redirectUrl={getApplicationPath(applicationId)}
            onClick={handleClick}
          />
        );

      case APPLICATION_STATUS.offered:
        return (
          <StatusButtonOrLabel
            disabled={cannotInteractWithOffers}
            disabledLabel={t`View offer(s)`}
            status={APPLICATION_STATUS.offered}
            variant="success"
            buttonText={hasMultipleOffers ? t`Review offers` : t`Review offer`}
            redirectUrl={getApplicationPath(applicationId)}
            onClick={handleClick}
          />
        );
      case APPLICATION_STATUS.conditional_offer:
        return (
          <StatusButtonOrLabel
            disabled={cannotInteractWithOffers}
            disabledLabel="View offer(s)"
            status={APPLICATION_STATUS.conditional_offer}
            variant="success"
            buttonText={
              hasMultipleOffers ? 'Conditional offers' : 'Conditional offer'
            }
            redirectUrl={getApplicationPath(applicationId)}
            onClick={handleClick}
          />
        );

      case APPLICATION_STATUS.funded:
        return <Funded application={application} showCommission={true} />;

      case APPLICATION_STATUS.declined:
      case APPLICATION_STATUS.offer_rejected:
      case APPLICATION_STATUS.deferred:
        return (
          <span className={styles.nonActionableStatus}>
            {APPLICATION_STATUS_VALUE(status)}
          </span>
        );

      case APPLICATION_STATUS.manual_review:
        return (
          <ManualReview
            hasDocumentFeedback={!!application.has_document_feedback}
          />
        );

      default:
        return (
          <span className={renderClass()}>
            {APPLICATION_STATUS_VALUE(status)}
          </span>
        );
    }
  };

  return (
    <div className={styles.applicationStatusContainer}>
      {renderStatus()}
      <OpenInNewTabButton application={application} />
    </div>
  );
};
